import { parseApiError } from "./errors.js";
import { type BroadcastState, broadcastStateFromJson } from "./types/broadcast.js";
import {
  type BroadcastOperatorStatus,
  broadcastOperatorStatusFromJson,
} from "./types/broadcast-operator-status.js";
import {
  type FeedbackApp,
  type FeedbackItem,
  type FeedbackKind,
  feedbackFromJson,
} from "./types/feedback.js";
import { type MetadataVocabLists, metadataVocabFromJson } from "./types/metadata-vocab.js";
import { type NowPlaying, nowPlayingFromJson } from "./types/now-playing.js";
import type {
  PresignUploadRequest,
  PresignUploadResponse,
  UploadObjectViaPresignInput,
} from "./upload.js";
import { withUploadRetry } from "./upload-retry.js";

export interface ResolveBaseUrlOptions {
  apiUrl?: string;
  isDev?: boolean;
  inBrowser?: boolean;
}

export function resolveBaseUrl(options: ResolveBaseUrlOptions = {}): string {
  const apiUrl = options.apiUrl ?? import.meta.env.VITE_API_URL ?? "";
  if (apiUrl.length > 0) {
    return apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
  }

  const inBrowser = options.inBrowser ?? typeof window !== "undefined";
  const isDev = options.isDev ?? import.meta.env.DEV;

  if (inBrowser && isDev) {
    const riqApp = (import.meta.env as { VITE_RIQ_APP?: string }).VITE_RIQ_APP;
    if (riqApp === "studio") {
      return "";
    }
    return "http://localhost:8787";
  }

  return "";
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetchFn?: typeof fetch;
}

/**
 * Stored `fetch` must call through globalThis each time (avoids "Illegal invocation" in bundled IIFEs).
 */
export function boundFetch(impl?: typeof fetch): typeof fetch {
  if (impl) return impl;
  return (input, init) => globalThis.fetch(input, init);
}

export class ApiClient {
  readonly resolvedBaseUrl: string;
  private readonly fetchFn: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.resolvedBaseUrl = options.baseUrl != null ? options.baseUrl : resolveBaseUrl();
    this.fetchFn = boundFetch(options.fetchFn);
  }

  get wsBaseUrl(): string {
    if (this.resolvedBaseUrl) {
      return this.resolvedBaseUrl.replace(/^http/, "ws");
    }
    if (typeof window !== "undefined") {
      return (window.location.protocol === "https:" ? "wss://" : "ws://") + window.location.host;
    }
    return "";
  }

  private url(path: string): string {
    return `${this.resolvedBaseUrl}${path}`;
  }

  private fetchInit(operator: boolean, init: RequestInit = {}): RequestInit {
    if (!operator) {
      return init;
    }
    return {
      ...init,
      credentials: "include",
    };
  }

  private async jsonHeaders(): Promise<Record<string, string>> {
    return {
      "Content-Type": "application/json",
    };
  }

  private async handleJsonResponse(
    method: string,
    path: string,
    response: Response,
  ): Promise<Record<string, unknown>> {
    const body = await response.text();
    if (response.status === 200) {
      return JSON.parse(body) as Record<string, unknown>;
    }
    parseApiError(method, path, response.status, body);
  }

  async getJson(path: string, options?: { operator?: boolean }): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(this.url(path), this.fetchInit(options?.operator ?? false));
    return this.handleJsonResponse("GET", path, response);
  }

  async getJsonList(path: string): Promise<unknown[]> {
    const response = await this.fetchFn(this.url(path));
    const body = await response.text();
    if (response.status === 200) {
      return JSON.parse(body) as unknown[];
    }
    parseApiError("GET", path, response.status, body);
  }

  async deleteJson(path: string, body?: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "DELETE",
        ...(body
          ? {
              headers: await this.jsonHeaders(),
              body: JSON.stringify(body),
            }
          : {}),
      }),
    );
    return this.handleJsonResponse("DELETE", path, response);
  }

  async postJson(
    path: string,
    body: Record<string, unknown>,
    options?: { operator?: boolean },
  ): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(options?.operator ?? true, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(body),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  async putFile(
    path: string,
    bytes: ArrayBuffer | Uint8Array,
    _filename: string,
    options?: { contentType?: string },
  ): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "PUT",
        headers: {
          "Content-Type": options?.contentType ?? "audio/mpeg",
        },
        body: bytes as BodyInit,
      }),
    );
    return this.handleJsonResponse("PUT", path, response);
  }

  async requestUploadPresign(input: PresignUploadRequest): Promise<PresignUploadResponse> {
    const data = await this.postJson("/api/admin/upload/presign", {
      kind: input.kind,
      filename: input.filename,
      ...(input.contentType ? { content_type: input.contentType } : {}),
      ...(input.seriesId ? { series_id: input.seriesId } : {}),
    });
    return {
      key: data.key as string,
      upload_url: data.upload_url as string,
      expires_at: data.expires_at as string,
      content_type: data.content_type as string,
    };
  }

  async putPresignedUrl(
    uploadUrl: string,
    body: Blob | ArrayBuffer | Uint8Array,
    contentType?: string,
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (contentType) {
      headers["Content-Type"] = contentType;
    }
    const response = await this.fetchFn(uploadUrl, {
      method: "PUT",
      headers,
      body: body as BodyInit,
    });
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Presigned upload failed (${response.status}): ${detail}`);
    }
  }

  async uploadObjectViaPresign(input: UploadObjectViaPresignInput): Promise<string> {
    return withUploadRetry(async () => {
      const presign = await this.requestUploadPresign({
        kind: input.kind,
        filename: input.filename,
        contentType: input.contentType,
        seriesId: input.seriesId,
      });
      await this.putPresignedUrl(
        presign.upload_url,
        input.body,
        input.contentType ?? presign.content_type,
      );
      return presign.key;
    });
  }

  async completeSeriesImageUpload(seriesId: string, key: string): Promise<Record<string, unknown>> {
    return this.postJson(`/api/admin/series/${seriesId}/image/complete`, { key });
  }

  async completeAlbumArtUpload(key: string): Promise<string> {
    const res = await this.postJson("/api/admin/metadata/album-art/complete", { key });
    return res.key as string;
  }

  async putJson(path: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "PUT",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(body),
      }),
    );
    return this.handleJsonResponse("PUT", path, response);
  }

  async patchJson(path: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "PATCH",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(body),
      }),
    );
    return this.handleJsonResponse("PATCH", path, response);
  }

  async fetchBroadcastState(): Promise<BroadcastState> {
    const path = "/api/broadcast";
    const response = await this.fetchFn(this.url(path));
    const body = await response.text();
    if (response.status === 200) {
      return broadcastStateFromJson(JSON.parse(body) as Record<string, unknown>);
    }
    parseApiError("GET", path, response.status, body);
  }

  async fetchBroadcastOperatorStatus(opts?: {
    skipProbe?: boolean;
  }): Promise<BroadcastOperatorStatus> {
    const path = opts?.skipProbe
      ? "/api/studio/broadcast/status?probe=skip"
      : "/api/studio/broadcast/status";
    const response = await this.fetchFn(this.url(path), this.fetchInit(true));
    const body = await response.text();
    if (response.status === 200) {
      return broadcastOperatorStatusFromJson(JSON.parse(body) as Record<string, unknown>);
    }
    parseApiError("GET", path, response.status, body);
  }

  async fetchNowPlaying(): Promise<NowPlaying> {
    const path = "/api/nowplaying";
    const response = await this.fetchFn(this.url(path));
    const body = await response.text();
    if (response.status === 200) {
      return nowPlayingFromJson(JSON.parse(body) as Record<string, unknown>);
    }
    parseApiError("GET", path, response.status, body);
  }

  async getStationPlaylist(): Promise<unknown[]> {
    const data = await this.getJson("/api/admin/station/playlist", { operator: true });
    return (data.playlist as unknown[] | undefined) ?? [];
  }

  async setStationPlaylist(recordingIds: string[]): Promise<boolean> {
    const data = await this.putJson("/api/admin/station/playlist", {
      recording_ids: recordingIds,
    });
    return (data.success as boolean | undefined) ?? false;
  }

  async listPlaylists(): Promise<unknown[]> {
    const data = await this.getJson("/api/admin/playlists", { operator: true });
    return (data.playlists as unknown[] | undefined) ?? [];
  }

  async createPlaylist(name: string): Promise<string> {
    const data = await this.postJson("/api/admin/playlists", { name });
    return data.id as string;
  }

  async renamePlaylist(id: string, name: string): Promise<boolean> {
    const data = await this.patchJson(`/api/admin/playlists/${id}`, { name });
    return (data.success as boolean | undefined) ?? false;
  }

  async deletePlaylist(id: string): Promise<boolean> {
    const data = await this.deleteJson(`/api/admin/playlists/${id}`);
    return (data.success as boolean | undefined) ?? false;
  }

  async getPlaylistItems(id: string): Promise<unknown[]> {
    const data = await this.getJson(`/api/admin/playlists/${id}/items`, { operator: true });
    return (data.items as unknown[] | undefined) ?? [];
  }

  async setPlaylistItems(id: string, recordingIds: string[]): Promise<boolean> {
    const data = await this.putJson(`/api/admin/playlists/${id}/items`, {
      recording_ids: recordingIds,
    });
    return (data.success as boolean | undefined) ?? false;
  }

  async getStationChain(): Promise<unknown[]> {
    const data = await this.getJson("/api/admin/station/chain", { operator: true });
    return (data.chain as unknown[] | undefined) ?? [];
  }

  async setStationChain(playlistIds: string[]): Promise<boolean> {
    const data = await this.putJson("/api/admin/station/chain", {
      playlist_ids: playlistIds,
    });
    return (data.success as boolean | undefined) ?? false;
  }

  async startBroadcast(
    sessionId: string,
    trackId: string,
    metadata?: { title?: string; speaker?: string },
    mid?: string | null,
  ): Promise<boolean> {
    const path = "/api/broadcast/start";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify({
          sessionId,
          trackId,
          ...(mid != null && mid !== "" ? { mid } : {}),
          ...(metadata?.title != null ? { title: metadata.title } : {}),
          ...(metadata?.speaker != null ? { speaker: metadata.speaker } : {}),
        }),
      }),
    );
    if (response.status === 200) {
      const data = (await response.json()) as { success?: boolean };
      return data.success ?? false;
    }
    return false;
  }

  async updateBroadcastMetadata(metadata: { title?: string; speaker?: string }): Promise<boolean> {
    const path = "/api/broadcast/metadata";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "PATCH",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(metadata),
      }),
    );
    if (response.status === 200) {
      const data = (await response.json()) as { success?: boolean };
      return data.success ?? false;
    }
    return false;
  }

  /**
   * Update a Recording. Field names are the wire (snake_case) shape the admin
   * route accepts, not the camelCase catalog shape.
   */
  async patchRecording(
    recordingId: string,
    patch: {
      title?: string;
      duration_seconds?: number;
      description?: string | null;
      speaker?: string | null;
      recorded_at?: string | null;
      sort_order?: number | null;
      duration_source?: string | null;
      duration_confidence?: string | null;
      tags?: string[];
      visible?: boolean;
    },
  ): Promise<boolean> {
    const data = await this.patchJson(`/api/admin/recordings/${recordingId}`, patch);
    return (data.success as boolean | undefined) ?? false;
  }

  async deleteRecording(recordingId: string): Promise<boolean> {
    const data = await this.deleteJson(`/api/admin/recordings/${recordingId}`);
    return (data.success as boolean | undefined) ?? false;
  }

  async getMetadataVocab(): Promise<MetadataVocabLists> {
    const raw = await this.getJson("/api/admin/metadata/vocab", { operator: true });
    return metadataVocabFromJson(raw);
  }

  async getLibraryTags(): Promise<string[]> {
    const data = await this.getJson("/api/admin/library/tags", { operator: true });
    return (data.tags as string[] | undefined) ?? [];
  }

  async addMetadataVocab(kind: string, value: string): Promise<boolean> {
    const data = await this.postJson("/api/admin/metadata/vocab", { kind, value });
    return (data.success as boolean | undefined) ?? false;
  }

  async removeMetadataVocab(kind: string, value: string): Promise<boolean> {
    const data = await this.deleteJson("/api/admin/metadata/vocab", { kind, value });
    return (data.success as boolean | undefined) ?? false;
  }

  async getAdminLibraryList(): Promise<unknown[]> {
    const path = "/api/admin/library";
    const response = await this.fetchFn(this.url(path), this.fetchInit(true));
    const body = await response.text();
    if (response.status === 200) {
      return JSON.parse(body) as unknown[];
    }
    parseApiError("GET", path, response.status, body);
  }

  async getAdminFeedbackList(params?: {
    app?: FeedbackApp;
    kind?: FeedbackKind;
  }): Promise<FeedbackItem[]> {
    const search = new URLSearchParams();
    if (params?.app) search.set("app", params.app);
    if (params?.kind) search.set("kind", params.kind);
    const suffix = search.size > 0 ? `?${search}` : "";
    const data = await this.getJson(`/api/admin/feedback${suffix}`, { operator: true });
    const rows = (data.items as unknown[] | undefined) ?? [];
    return rows.map((row) => feedbackFromJson(row as Record<string, unknown>));
  }

  async stopBroadcast(): Promise<boolean> {
    const path = "/api/broadcast/stop";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "POST",
        headers: await this.jsonHeaders(),
      }),
    );
    if (response.status === 200) {
      const data = (await response.json()) as { success?: boolean };
      return data.success ?? false;
    }
    return false;
  }

  async fetchFeedbackConfig(): Promise<{ turnstileSiteKey: string | null }> {
    const data = await this.getJson("/api/feedback/config");
    const siteKey = data.turnstileSiteKey;
    return {
      turnstileSiteKey: typeof siteKey === "string" && siteKey.length > 0 ? siteKey : null,
    };
  }

  async submitFeedback(payload: {
    kind: "issue" | "suggestion";
    message: string;
    app: "studio" | "tuner";
    page_url?: string;
    turnstile_token: string;
  }): Promise<boolean> {
    const path = "/api/feedback";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(false, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(payload),
      }),
    );
    if (response.status === 200) {
      const data = (await response.json()) as { success?: boolean };
      return data.success ?? false;
    }
    return false;
  }

  /**
   * Create an ingest Session. Pass an offer only when warming a connection
   * ahead of go-live — the cold publish path sends no SDP, because the SFU is
   * far faster when `tracks/new` negotiates a session with no PeerConnection.
   */
  async createIngestSession(sdpOffer?: string): Promise<Record<string, unknown>> {
    const path = "/api/studio/session/new";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify(
          sdpOffer != null ? { sessionDescription: { type: "offer", sdp: sdpOffer } } : {},
        ),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  /**
   * Create the listener Session and subscribe it to the live Broadcast in one
   * request. Halves the client round trips before audio, and the server picks
   * the live session/track so a stale local snapshot cannot be used.
   */
  async subscribeToBroadcast(): Promise<Record<string, unknown>> {
    const path = "/api/session/subscribe";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(false, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: "{}",
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  /**
   * The session id travels in the body, not the path: a per-session URL makes
   * the CORS preflight uncacheable, which costs the Android Tuner an extra
   * round trip on every join. POST for the same reason — PUT is never simple.
   */
  async renegotiateSession(sessionId: string, sdpAnswer: string): Promise<Record<string, unknown>> {
    const path = "/api/session/renegotiate";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(false, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify({
          sessionId,
          sessionDescription: {
            type: "answer",
            sdp: sdpAnswer,
          },
        }),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  /**
   * Publish the Studio's metadata channel on the SFU. Returns the SCTP stream
   * id to bind a `negotiated` RTCDataChannel to — the SFU never fires
   * `ondatachannel`.
   */
  async publishMetadataChannel(sessionId: string): Promise<Record<string, unknown>> {
    return this.postJson("/api/studio/session/datachannels/new", { sessionId });
  }

  /** Give a listener session an SCTP transport before it subscribes to a channel. */
  async establishDataChannelTransport(sessionId: string): Promise<Record<string, unknown>> {
    const path = "/api/session/datachannels/establish";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(false, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify({ sessionId }),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  /** Subscribe a listener session to the live Broadcast's metadata channel. */
  async subscribeMetadataChannel(sessionId: string): Promise<Record<string, unknown>> {
    const path = "/api/session/datachannels/subscribe";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(false, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify({ sessionId }),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }

  /** Publish the Studio track and register the Broadcast in one request. */
  async goLive(params: {
    sessionId?: string;
    sdpOffer?: string;
    tracks: Record<string, unknown>[];
    metadata?: { title?: string; speaker?: string };
  }): Promise<Record<string, unknown>> {
    const path = "/api/studio/broadcast/go-live";
    const response = await this.fetchFn(
      this.url(path),
      this.fetchInit(true, {
        method: "POST",
        headers: await this.jsonHeaders(),
        body: JSON.stringify({
          ...(params.sessionId ? { sessionId: params.sessionId } : {}),
          ...(params.sdpOffer ? { sdpOffer: params.sdpOffer } : {}),
          tracks: params.tracks,
          ...(params.metadata?.title != null ? { title: params.metadata.title } : {}),
          ...(params.metadata?.speaker != null ? { speaker: params.metadata.speaker } : {}),
        }),
      }),
    );
    return this.handleJsonResponse("POST", path, response);
  }
}
